import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { useAuth0 } from './hooks/useAuthPatched';
import { ToastContainer } from 'react-toastify';
import { ThemeProvider } from './context/ThemeContext';
import { CryptoMarketProvider } from './context/CryptoMarketContext';
import { UserProfileProvider } from './hooks/useUserProfile';
import { KYCProvider } from './hooks/useKYC';
import { ToastProvider } from './context/ToastContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import ProfileRequiredRoute from './components/Auth/ProfileRequiredRoute';
import KYCRequiredRoute from './components/Auth/KYCRequiredRoute';
import SessionWarning from './components/Auth/SessionWarning';
import { MockAuth0Provider, isE2ETestMode } from './components/Auth/MockAuth0Provider';
import Loading from './components/Loading';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { initErrorMonitoring, createErrorHandler, setupGlobalErrorHandlers } from './services/errorMonitoring';
import './App.css';

import './version';
import config from './config';

// Initialize error monitoring (CloudWatch RUM) before app renders
initErrorMonitoring({
  appId: process.env.REACT_APP_RUM_APP_ID || '',
  identityPoolId: process.env.REACT_APP_RUM_IDENTITY_POOL_ID || '',
  region: process.env.REACT_APP_RUM_REGION || 'us-east-2',
  environment: config.environment || 'development',
});

// Set up global error handlers for comprehensive error capture
// Captures window.onerror and unhandledrejection events
setupGlobalErrorHandlers();

// ============================================================================
// Route-based Code Splitting
// Heavy page components are lazy-loaded to reduce initial bundle size
// ============================================================================

// Core pages - lazy loaded for code splitting
const HomePage = lazy(() => import('./components/HomePage/HomePage'));
const TradingPageV2 = lazy(() => import('./pages/TradingPageV2'));
const TradingPageV3 = lazy(() => import('./pages/TradingPageV3'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const SettingsPageEnhanced = lazy(() => import('./pages/SettingsPageEnhanced'));
const PortfolioAnalytics = lazy(() => import('./components/PortfolioAnalytics/PortfolioAnalytics'));
const TaxReportsPage = lazy(() => import('./pages/TaxReportsPage'));
const CallbackPage = lazy(() => import('./components/Auth/CallbackPage'));

// UI components - can be lazy loaded since they're not critical for initial render
const EnhancedNavBarSimple = lazy(() => import('./components/NavBar/EnhancedNavBarSimple'));
const CryptoTickerEnhanced = lazy(() => import('./components/CryptoTicker/CryptoTickerEnhanced'));

// Inner component to access useAuth0 and useLocation hooks
const AppContent = () => {
    const { isAuthenticated } = useAuth0();
    const location = useLocation();

    // Show ticker on authenticated pages only (not on home page or callback)
    const showTicker = isAuthenticated && location.pathname !== '/' && location.pathname !== '/auth/callback';

    return (
        <>
            <Suspense fallback={null}>
                <EnhancedNavBarSimple />
            </Suspense>
            {showTicker && (
                <Suspense fallback={<div className="h-10 bg-gray-100 dark:bg-gray-800 animate-pulse" />}>
                    <CryptoTickerEnhanced />
                </Suspense>
            )}
            <Suspense fallback={<Loading variant="page" fullPage />}>
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/auth/callback" element={<CallbackPage />} />
                    <Route
                        path="/trade"
                        element={
                            <ProtectedRoute>
                                <ProfileRequiredRoute>
                                    <KYCRequiredRoute>
                                        <TradingPageV3 />
                                    </KYCRequiredRoute>
                                </ProfileRequiredRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/trade-v2"
                        element={
                            <ProtectedRoute>
                                <ProfileRequiredRoute>
                                    <KYCRequiredRoute>
                                        <TradingPageV2 />
                                    </KYCRequiredRoute>
                                </ProfileRequiredRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute>
                                <ProfileRequiredRoute>
                                    <KYCRequiredRoute softBlock>
                                        <DashboardPage />
                                    </KYCRequiredRoute>
                                </ProfileRequiredRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/portfolio-analytics"
                        element={
                            <ProtectedRoute>
                                <ProfileRequiredRoute>
                                    <KYCRequiredRoute softBlock>
                                        <PortfolioAnalytics />
                                    </KYCRequiredRoute>
                                </ProfileRequiredRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/settings/*"
                        element={
                            <ProtectedRoute>
                                <SettingsPageEnhanced />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/tax-reports"
                        element={
                            <ProtectedRoute>
                                <ProfileRequiredRoute>
                                    <KYCRequiredRoute softBlock>
                                        <TaxReportsPage />
                                    </KYCRequiredRoute>
                                </ProfileRequiredRoute>
                            </ProtectedRoute>
                        }
                    />
                </Routes>
            </Suspense>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="dark"
                style={{
                    zIndex: 9999,
                }}
                toastStyle={{
                    borderRadius: '4px',
                    fontSize: '14px',
                }}
            />
            <SessionWarning />
        </>
    );
};

const App = () => {
    // Use config for origin instead of hardcoding
    const origin = window.location.origin.includes('localhost')
        ? 'http://localhost:3000'
        : window.location.origin;

    // Configuration logging removed for security - sensitive data should not be logged

    // Check if E2E test mode is enabled
    const useE2ETestMode = isE2ETestMode();

    // Inner content that's shared between real and mock auth
    const appContent = (
        <ThemeProvider>
            <CryptoMarketProvider>
                <UserProfileProvider>
                    <KYCProvider>
                        <ToastProvider>
                            <ErrorBoundary
                                onError={createErrorHandler()}
                                showError={config.environment !== 'production'}
                            >
                                <Router>
                                    <AppContent />
                                </Router>
                            </ErrorBoundary>
                        </ToastProvider>
                    </KYCProvider>
                </UserProfileProvider>
            </CryptoMarketProvider>
        </ThemeProvider>
    );

    // In E2E test mode, wrap with both providers (MockAuth0Provider handles the auth)
    // Real Auth0Provider is still needed for components that import useAuth0 directly
    if (useE2ETestMode) {
        return (
            <Auth0Provider
                domain={config.auth0.domain}
                clientId={config.auth0.clientId}
                authorizationParams={{
                    redirect_uri: `${origin}${config.auth0.callbackPath}`,
                    audience: config.auth0.audience,
                    scope: "openid profile email",
                }}
                logoutParams={{
                    returnTo: origin
                }}
                cacheLocation="localstorage"
                useRefreshTokens={true}
            >
                <MockAuth0Provider>
                    {appContent}
                </MockAuth0Provider>
            </Auth0Provider>
        );
    }

    return (
        <Auth0Provider
            domain={config.auth0.domain}
            clientId={config.auth0.clientId}
            authorizationParams={{
                redirect_uri: `${origin}${config.auth0.callbackPath}`,
                audience: config.auth0.audience,
                scope: "openid profile email",
            }}
            logoutParams={{
                returnTo: origin
            }}
            cacheLocation="localstorage"
            useRefreshTokens={true}
        >
            {appContent}
        </Auth0Provider>
    );
};

export default App;
